/**
 * 🏢 Corporate Accounts Management Page
 * Manage corporate accounts, approvals, and credit limits
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  Typography,
  Tabs,
  Tab,
  Button,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  TextField,
  InputAdornment,
  Alert,
  Tooltip,
  TablePagination,
} from '@mui/material';
import {
  Business,
  Add,
  Search,
  Visibility,
  CheckCircle,
  Cancel,
  Refresh,
  Handshake,
} from '@mui/icons-material';
import { usePermissions } from '@/hooks/usePermissions';
import {
  corporateAccountsService,
  CorporateAccount,
  CorporateAccountStatus,
} from '@/services/corporateAccountsService';
import { CorporateAccountDetailsModal } from '@/components/corporate/CorporateAccountDetailsModal';
import { ApproveCorporateAccountModal } from '@/components/corporate/ApproveCorporateAccountModal';
import { RejectCorporateAccountModal } from '@/components/corporate/RejectCorporateAccountModal';
import { RegisterCorporateAccountModal } from '@/components/corporate/RegisterCorporateAccountModal';
import { ActivatePartnershipModal } from '@/components/corporate/ActivatePartnershipModal';
import toast from 'react-hot-toast';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => {
  return (
    <div role="tabpanel" hidden={value !== index}>
      {value === index && <Box>{children}</Box>}
    </div>
  );
};

const getStatusColor = (status: CorporateAccountStatus) => {
  switch (status) {
    case 'active':
      return 'success';
    case 'pending':
      return 'warning';
    case 'suspended':
      return 'error';
    case 'rejected':
      return 'default';
    default:
      return 'default';
  }
};

const formatCurrency = (amount: number | undefined | null, currency: string = 'NGN') => {
  if (amount === undefined || amount === null || isNaN(amount)) {
    // Return appropriate zero based on currency
    return currency === 'NGN' ? '₦0.00' : '$0.00';
  }

  // Use appropriate locale based on currency
  const locale = currency === 'NGN' ? 'en-NG' : 'en-US';

  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: currency,
  }).format(amount);
};

const formatDate = (dateString?: string) => {
  if (!dateString) return 'N/A';
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

export const CorporateAccountsPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [accounts, setAccounts] = useState<CorporateAccount[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [selectedAccount, setSelectedAccount] = useState<CorporateAccount | null>(null);
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [approveModalOpen, setApproveModalOpen] = useState(false);
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [registerModalOpen, setRegisterModalOpen] = useState(false);
  const [partnershipModalOpen, setPartnershipModalOpen] = useState(false);

  const { isSuperAdmin, can } = usePermissions();

  // Map tab index to status filter
  const getStatusFilter = (tabIndex: number): CorporateAccountStatus | 'all' => {
    switch (tabIndex) {
      case 0: return 'all';
      case 1: return 'pending';
      case 2: return 'active';
      case 3: return 'suspended';
      default: return 'all';
    }
  };

  // Check permissions (backend uses 'corporate' as resource name)
  if (!isSuperAdmin() && !can('corporate', 'view')) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          Access Denied: You don't have permission to view corporate accounts.
        </Alert>
      </Box>
    );
  }

  useEffect(() => {
    loadAccounts();
  }, [activeTab, page, rowsPerPage, searchQuery]);

  const loadAccounts = async () => {
    setLoading(true);
    try {
      const response = await corporateAccountsService.getCorporateAccounts({
        page: page + 1, // API uses 1-based indexing
        limit: rowsPerPage,
        status: getStatusFilter(activeTab),
        search: searchQuery || undefined,
      });

      if (response.success && response.data) {
        setAccounts(response.data.accounts);
        setTotalCount(response.data.pagination?.totalCount ?? 0);
        // Empty results are normal, not an error - don't show error toast
      } else if (response.error) {
        // Only show error toast if there's an actual error
        toast.error(response.error);
        setAccounts([]);
        setTotalCount(0);
      }
    } catch (error) {
      console.error('Failed to load corporate accounts:', error);
      toast.error('Failed to load corporate accounts');
      setAccounts([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
    setPage(0); // Reset to first page when changing tabs
  };

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
    setPage(0); // Reset to first page when searching
  };

  const handleViewDetails = (account: CorporateAccount) => {
    setSelectedAccount(account);
    setDetailsModalOpen(true);
  };

  const handleCloseDetailsModal = () => {
    setDetailsModalOpen(false);
    setSelectedAccount(null);
  };

  const handleDetailsUpdate = () => {
    loadAccounts(); // Refresh the list
  };

  const handleApprove = (account: CorporateAccount) => {
    setSelectedAccount(account);
    setApproveModalOpen(true);
  };

  const handleReject = (account: CorporateAccount) => {
    setSelectedAccount(account);
    setRejectModalOpen(true);
  };

  const handleCloseApproveModal = () => {
    setApproveModalOpen(false);
    setSelectedAccount(null);
  };

  const handleCloseRejectModal = () => {
    setRejectModalOpen(false);
    setSelectedAccount(null);
  };

  const handleApprovalSuccess = () => {
    loadAccounts(); // Refresh the list
  };

  const handleRejectionSuccess = () => {
    loadAccounts(); // Refresh the list
  };

  const handleActivatePartnership = (account: CorporateAccount) => {
    setSelectedAccount(account);
    setPartnershipModalOpen(true);
  };

  const handleClosePartnershipModal = () => {
    setPartnershipModalOpen(false);
    setSelectedAccount(null);
  };

  const handlePartnershipActivationSuccess = () => {
    loadAccounts(); // Refresh the list
  };

  const handleOpenRegisterModal = () => {
    setRegisterModalOpen(true);
  };

  const handleCloseRegisterModal = () => {
    setRegisterModalOpen(false);
  };

  const handleRegistrationSuccess = () => {
    loadAccounts(); // Refresh the list
  };

  const renderAccountsTable = () => {
    if (loading && accounts.length === 0) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      );
    }

    if (accounts.length === 0) {
      return (
        <Box sx={{ p: 4, textAlign: 'center' }}>
          <Business sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary">
            No corporate accounts found
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {searchQuery
              ? 'Try adjusting your search criteria'
              : 'Get started by registering a new corporate account'}
          </Typography>
        </Box>
      );
    }

    return (
      <>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Company Name</TableCell>
                <TableCell>Contact Person</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Credit Limit</TableCell>
                <TableCell align="right">Available Credit</TableCell>
                <TableCell align="right">Current Balance</TableCell>
                <TableCell>Created Date</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {accounts.map((account) => (
                <TableRow key={account.id} hover>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Box flex={1}>
                        <Typography variant="body2" fontWeight="medium">
                          {account.companyName}
                        </Typography>
                        {account.registrationNumber && (
                          <Typography variant="caption" color="text.secondary">
                            {account.registrationNumber}
                          </Typography>
                        )}
                      </Box>
                      {account.isPartnership && (
                        <Chip
                          icon={<Handshake fontSize="small" />}
                          label={account.partnershipType?.replace('_', ' ').toUpperCase() || 'PARTNERSHIP'}
                          size="small"
                          color="info"
                          sx={{ fontSize: '0.7rem' }}
                        />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {account.contactPerson?.name || 'N/A'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {account.contactPerson?.email || 'N/A'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={account.status.toUpperCase()}
                      color={getStatusColor(account.status)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="right">
                    {formatCurrency(account.creditLimit, account.currency)}
                  </TableCell>
                  <TableCell align="right">
                    {formatCurrency(account.availableCredit, account.currency)}
                  </TableCell>
                  <TableCell align="right">
                    {formatCurrency(account.currentBalance, account.currency)}
                  </TableCell>
                  <TableCell>
                    {formatDate(account.createdAt)}
                  </TableCell>
                  <TableCell align="center">
                    <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5 }}>
                      <Tooltip title="View Details">
                        <IconButton
                          size="small"
                          onClick={() => handleViewDetails(account)}
                        >
                          <Visibility fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      {account.status === 'pending' && (
                        <>
                          <Tooltip title="Approve">
                            <IconButton
                              size="small"
                              color="success"
                              onClick={() => handleApprove(account)}
                            >
                              <CheckCircle fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Reject">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleReject(account)}
                            >
                              <Cancel fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}
                      {account.status === 'active' && (
                        <Tooltip title={account.isPartnership ? "View Partnership" : "Activate Partnership"}>
                          <IconButton
                            size="small"
                            color={account.isPartnership ? "info" : "primary"}
                            onClick={() => handleActivatePartnership(account)}
                          >
                            <Handshake fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={totalCount}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </>
    );
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Business sx={{ fontSize: 40, color: 'primary.main' }} />
          <Box>
            <Typography variant="h4" gutterBottom>
              Corporate Accounts
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Manage corporate accounts, approvals, and credit limits
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Refresh">
            <IconButton onClick={loadAccounts} disabled={loading}>
              <Refresh />
            </IconButton>
          </Tooltip>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={handleOpenRegisterModal}
          >
            Register New Account
          </Button>
        </Box>
      </Box>

      {/* Search Bar */}
      <Box sx={{ mb: 3 }}>
        <TextField
          fullWidth
          placeholder="Search by company name, contact name, or email..."
          value={searchQuery}
          onChange={handleSearchChange}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      {/* Tabs & Table */}
      <Card>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          sx={{
            borderBottom: 1,
            borderColor: 'divider',
            px: 2,
          }}
        >
          <Tab label="All Accounts" />
          <Tab label="Pending Approval" />
          <Tab label="Active" />
          <Tab label="Suspended" />
        </Tabs>

        {/* Single tab panel for all tabs since the table content is the same */}
        <Box>
          {renderAccountsTable()}
        </Box>
      </Card>

      {/* Details Modal */}
      <CorporateAccountDetailsModal
        open={detailsModalOpen}
        account={selectedAccount}
        onClose={handleCloseDetailsModal}
        onUpdate={handleDetailsUpdate}
      />

      {/* Approve Modal */}
      <ApproveCorporateAccountModal
        open={approveModalOpen}
        account={selectedAccount}
        onClose={handleCloseApproveModal}
        onApprove={handleApprovalSuccess}
      />

      {/* Reject Modal */}
      <RejectCorporateAccountModal
        open={rejectModalOpen}
        account={selectedAccount}
        onClose={handleCloseRejectModal}
        onReject={handleRejectionSuccess}
      />

      {/* Register Modal */}
      <RegisterCorporateAccountModal
        open={registerModalOpen}
        onClose={handleCloseRegisterModal}
        onRegister={handleRegistrationSuccess}
      />

      {/* Activate Partnership Modal */}
      <ActivatePartnershipModal
        open={partnershipModalOpen}
        account={selectedAccount}
        onClose={handleClosePartnershipModal}
        onActivate={handlePartnershipActivationSuccess}
      />
    </Box>
  );
};
